import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { PushToAccountToggle } from "@/components/PushToAccountToggle";
import {
  getBondGia,
  giaBasisLabel,
  giaHasUnsigned,
  giaIsEditable,
  giaSentLine,
  giaServerErrors,
  giaSignerChip,
  giaStatusLabel,
  readGiaStatus,
  remindBondGia,
  resendBondGia,
  saveGiaSigners,
  sendBondGia,
  validateGiaEmails,
  type BondGiaSummary,
  type GiaRead,
  type GiaStatus,
} from "@/lib/brokerGia";
import { buttonClass } from "@/components/buttonStyles";
import { statusBadgeClass } from "@/lib/listFormat";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { messageFor } from "@/lib/errors";

// BRK-S182 — the GIA section. ONE component, and ONE host since BRK-S452: the
// submission page's GIA Form dialog. The reminder (PMS-S486) deep-links there
// with `?focus=gia`, which opens that dialog.
//
// ── NO OPTIMISTIC SUCCESS (the BRK-S122 class) ───────────────────────────────
//
// The chip is drawn from the SERVER's read and nothing else. "Send GIA Form"
// saves the emails, then sends, and the section moves to Sent only
// when the send answers 2xx with a read whose status is `sent`. A refusal, a
// network failure or a 2xx that does not say `sent` keeps the button live and
// says why. There is no local "sent" flag to forget to roll back.
//
// ── ERRORS LIVE ON THE ROW THEY ARE ABOUT (BRK-S181 revision 4) ──────────────
//
// A missing, malformed or shared email is about a PERSON, so it renders inside
// that person's row — the client's check and the server's 400 alike. Only a
// refusal that names nobody (a 409, a network failure) sits beside the button.
//
// ── IN THE GIA FORM DIALOG (BRK-S423, BRK-S452) ─────────────────────────────
//
// The submission page's GIA Form button opens this section in a dialog
// (`dialog`), the signer rows' ONE home: since BRK-S452/BRK-S502 nothing else
// hosts it — the Required steps panel's embedded copy and the standalone card
// are gone. There it also offers what the
// wireframe's pop-up does: "Send GIA Form" before the GIA goes out; "Send
// reminder" (to the signers not served as signed) and "Resend GIA Form" (a
// void-and-resend, confirmed first, because everyone signs the new one) while
// it is out; "Close" once it is signed or on file. Each says it worked only off
// its own 2xx read-back — a reminder off a `lastReminderAt`, a resend off a
// `sent` read — and then asks the page to re-read the order, so the button
// moves on the server's word too. A GIA on file (`not_required`) is shown, read
// only, rather than rendering nothing: the button that opened it is there.

/** The `?focus=` value that means "the GIA section" — half of an agreement
 *  with PMS's reminder template, so a constant rather than an inline string. */
export const GIA_FOCUS = "gia";

/** The chip palette, borrowed from the shared list badges so a signed GIA
 *  reads the same green as any other finished thing. */
function chipClass(status: GiaStatus | null): string {
  switch (status) {
    case "signed":
    case "not_required":
      return statusBadgeClass("approved");
    case "sent":
      return statusBadgeClass("submitted");
    case "declined":
      return statusBadgeClass("declined");
    default:
      return statusBadgeClass("draft");
  }
}

/** The dialog's hooks into the page (BRK-S423). */
export interface GiaDialogHost {
  /** A write answered 2xx: re-read the order so the button follows. */
  onChanged: () => void;
  /** The signed state's Close. */
  onClose: () => void;
  /** The order has ended: show where the GIA stands, offer nothing. */
  readOnly?: boolean;
}

/** What a dialog action confirms, said only off its 2xx read-back. */
const CONFIRMED = {
  send: "GIA Form sent to the indemnitors for signature.",
  remind: "Reminder sent to the indemnitors who haven’t signed.",
  resend:
    "A new GIA Form went to every indemnitor. The one sent before is void.",
} as const;

export function GiaSection({
  orderId,
  summary,
  focus = false,
  dialog,
}: {
  orderId: string;
  /** The order read's `gia` block — the chip until the section's own read
   *  lands. */
  summary: BondGiaSummary;
  /** Scroll to the section and focus it once loaded (`?focus=gia`). */
  focus?: boolean;
  /** The GIA Form dialog it lives in (BRK-S423): the reminder, the resend,
   *  Close, and the page's re-read. */
  dialog: GiaDialogHost;
}) {
  const { authedRequest } = useAuth();
  const [gia, setGia] = useState<GiaRead | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [confirmed, setConfirmed] = useState<string | null>(null);
  // The resend voids what is out, so it is asked twice.
  const [confirmingResend, setConfirmingResend] = useState(false);
  // The wizard's choice, asked the same way and defaulting the same way: ON.
  const [pushToAccount, setPushToAccount] = useState(true);

  const section = useRef<HTMLElement>(null);
  const focused = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getBondGia(authedRequest, orderId)
      .then((read) => {
        if (cancelled) return;
        setGia(read);
        setEmails(
          Object.fromEntries(
            read.signers.map((s) => [s.ownerId, s.email ?? ""]),
          ),
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(messageFor(err));
      });
    return () => {
      cancelled = true;
    };
  }, [authedRequest, orderId, attempt]);

  const status = gia !== null ? gia.status : readGiaStatus(summary.status);
  const readOnly = dialog.readOnly ?? false;
  const editable = gia !== null && !readOnly && giaIsEditable(status);
  // The section's own read once it lands — a resend's null reminder must not
  // fall back to the order summary's older date — and the summary until then.
  const sentLine = giaSentLine(
    gia ?? {
      sentAt: summary.sentAt,
      lastReminderAt: summary.lastReminderAt ?? null,
    },
  );

  // ONCE, when there is something to land on: the email to fill in when the
  // GIA can still be sent, otherwise the heading.
  useEffect(() => {
    if (!focus || focused.current || (gia === null && loadError === null)) {
      return;
    }
    focused.current = true;
    section.current?.scrollIntoView({ block: "start" });
    const input = editable
      ? section.current?.querySelector<HTMLInputElement>("input")
      : null;
    // The dialog's title names the GIA: the section itself takes the focus.
    (input ?? section.current)?.focus();
  }, [focus, gia, loadError, editable]);

  async function send() {
    if (!gia || busy) return;
    const confirmed = gia.signers.map((s) => ({
      ownerId: s.ownerId,
      email: (emails[s.ownerId] ?? "").trim(),
    }));
    const invalid = validateGiaEmails(confirmed);
    setRowErrors(invalid);
    setSummaryError(null);
    if (Object.keys(invalid).length > 0) return;

    setBusy(true);
    const ownerIds = gia.signers.map((s) => s.ownerId);
    try {
      await saveGiaSigners(authedRequest, orderId, confirmed, pushToAccount);
      const read = await sendBondGia(authedRequest, orderId);
      if (read.status !== "sent") {
        // A 2xx that does not say sent is NOT a send. Keep the button live.
        setSummaryError(
          "We couldn’t confirm the GIA was sent. Please try again.",
        );
        return;
      }
      setGia(read);
      setConfirmed(CONFIRMED.send);
      dialog.onChanged();
    } catch (err) {
      const placed = giaServerErrors(err, ownerIds);
      setRowErrors(placed.byOwner);
      setSummaryError(placed.summary);
    } finally {
      setBusy(false);
    }
  }

  /** The reminder and the resend: the write, then its read-back judged. */
  async function act(kind: "remind" | "resend") {
    if (!gia || busy) return;
    setBusy(true);
    setSummaryError(null);
    setConfirmed(null);
    try {
      const read =
        kind === "remind"
          ? await remindBondGia(authedRequest, orderId)
          : await resendBondGia(authedRequest, orderId);
      // A reminder is confirmed by the date it went; a resend by a GIA that
      // is out again. A 2xx that says neither is not a success.
      const worked =
        kind === "remind"
          ? read.lastReminderAt !== null && read.status === "sent"
          : read.status === "sent";
      if (!worked) {
        setSummaryError(
          kind === "remind"
            ? "We couldn’t confirm the reminder went. Please try again."
            : "We couldn’t confirm the GIA was sent again. Please try again.",
        );
        return;
      }
      setGia(read);
      setConfirmingResend(false);
      setConfirmed(CONFIRMED[kind]);
      dialog.onChanged();
    } catch (err) {
      // Nothing here names a signer, so whatever PMS said goes beside the
      // buttons (`GIA_ALREADY_OPEN` and the rest, in the portal's copy).
      setSummaryError(giaServerErrors(err, []).summary);
    } finally {
      setBusy(false);
    }
  }

  const out = gia !== null && !readOnly && status === "sent";
  const done = status === "signed" || status === "not_required";

  return (
    <section
      ref={section}
      id="section-gia"
      aria-label="General Indemnity Agreement"
      tabIndex={-1}
      className="flex w-full min-w-0 flex-col gap-4 text-left outline-none"
    >
      {/* In the dialog the title names the GIA, so only the chip is drawn. */}
      <span
        data-testid="gia-status"
        className={`inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${chipClass(status)}`}
      >
        {status === "not_required" ? "On file" : giaStatusLabel(status)}
      </span>

      <p className="text-sm text-brk-dim">
        Every listed owner, and every listed owner’s spouse, signs the General
        Indemnity Agreement before any bond is issued. We email each person a
        link to sign.
      </p>
      {(gia?.requiredReason ?? summary.requiredReason) ===
        "indemnitors_changed" && (
        <p className="text-sm text-brk-dim">
          The owners have changed since the last GIA was signed, so a new one is
          needed.
        </p>
      )}

      {status === "sent" && sentLine !== null && (
        <p data-testid="gia-sent-line" className="text-sm text-brk-text">
          {sentLine}
        </p>
      )}
      {status === "not_required" && (
        <p className="text-sm text-brk-text">
          A GIA on file covers this request — every indemnitor has signed it.
        </p>
      )}
      {status === "signed" && (gia?.signedAt ?? summary.signedAt) && (
        <p className="text-sm text-brk-text">
          Signed by everyone{" "}
          {formatBondDate(gia?.signedAt ?? summary.signedAt ?? "")}.
        </p>
      )}

      {loadError !== null && (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-brk-danger">
            {loadError}
          </p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
          >
            Try again
          </button>
        </div>
      )}
      {gia === null && loadError === null && (
        <p role="status" className="text-sm text-brk-dim">
          Loading signers…
        </p>
      )}

      {gia !== null && gia.signers.length === 0 && (
        <p className="text-sm text-brk-dim">
          No signers are listed on this GIA yet. Message Signal if that looks
          wrong.
        </p>
      )}
      {gia !== null && gia.signers.length > 0 && (
        <ul aria-label="Signers" className="flex flex-col gap-3">
          {gia.signers.map((signer) => {
            const chip = giaSignerChip(signer);
            return (
              <li
                key={signer.ownerId}
                data-testid={`gia-signer-${signer.ownerId}`}
                className="flex min-w-0 flex-col gap-2 rounded-lg border border-brk-border p-3"
              >
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-col">
                    <span className="break-words font-medium text-brk-text">
                      {signer.name}
                    </span>
                    <span className="text-sm text-brk-dim">
                      {giaBasisLabel(signer)}
                    </span>
                  </div>
                  <span
                    data-testid="signer-sign-status"
                    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                      chip.signed
                        ? "approved"
                        : signer.status === "not_signed"
                          ? "declined"
                          : "draft",
                    )}`}
                  >
                    {chip.label}
                  </span>
                </div>
                {editable ? (
                  <TextField
                    label={`Email for ${signer.name}`}
                    type="email"
                    autoComplete="off"
                    required
                    value={emails[signer.ownerId] ?? ""}
                    error={rowErrors[signer.ownerId]}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEmails((all) => ({ ...all, [signer.ownerId]: value }));
                      setRowErrors((all) => {
                        const rest = { ...all };
                        delete rest[signer.ownerId];
                        return rest;
                      });
                    }}
                  />
                ) : (
                  <p className="break-all text-sm text-brk-text">
                    {signer.email ?? "No email on file"}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editable && (
        <PushToAccountToggle on={pushToAccount} onChange={setPushToAccount} />
      )}

      {editable && (
        <div className="flex flex-col items-start gap-2">
          {summaryError !== null && (
            <p role="alert" className="text-sm text-brk-danger">
              {summaryError}
            </p>
          )}
          <button
            type="button"
            onClick={() => void send()}
            disabled={busy}
            className="w-full rounded-md bg-brk-accent px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {busy ? "Sending…" : "Send GIA Form"}
          </button>
        </div>
      )}

      {confirmed !== null && (
        <p role="status" className="text-sm text-brk-text">
          ✓ {confirmed}
        </p>
      )}

      {/* The dialog's actions while the GIA is out (BRK-S423). */}
      {out && (
        <div className="flex flex-col items-stretch gap-2">
          {summaryError !== null && (
            <p role="alert" className="text-sm text-brk-danger">
              {summaryError}
            </p>
          )}
          {confirmingResend ? (
            <div
              role="group"
              aria-label="Resend the GIA Form"
              className="flex flex-col gap-3 rounded-lg border border-brk-border p-3"
            >
              <p className="text-sm text-brk-text">
                Resending voids the GIA Form that’s out and sends a new one.
                Everyone signs the new one, including anyone who already has.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => void act("resend")}
                  disabled={busy}
                  className={buttonClass()}
                >
                  {busy ? "Sending…" : "Void and resend"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingResend(false)}
                  disabled={busy}
                  className={buttonClass({ variant: "secondary" })}
                >
                  Keep the one that’s out
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {giaHasUnsigned(gia.signers) && (
                <button
                  type="button"
                  onClick={() => void act("remind")}
                  disabled={busy}
                  className={buttonClass()}
                >
                  {busy ? "Sending…" : "Send reminder"}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setConfirmed(null);
                  setSummaryError(null);
                  setConfirmingResend(true);
                }}
                disabled={busy}
                className={buttonClass({ variant: "secondary" })}
              >
                Resend GIA Form
              </button>
            </div>
          )}
        </div>
      )}

      {done && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={dialog.onClose}
            className={buttonClass({ variant: "secondary" })}
          >
            Close
          </button>
        </div>
      )}
    </section>
  );
}
